import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment } from 'react';
import { defineMessages } from 'react-intl';

import { LoadMoreButton } from '@sorare/core/src/atoms/buttons/LoadMoreButton';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { EmptyContent } from '@sorare/core/src/contexts/intl/EmptyContent';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { Auction } from './Auction';
import { Bid } from './Bid';
import { Offer } from './Offer';
import { PrimaryOffer } from './PrimaryOffer';
import type {
  SpentFiatPaymentIntentsQuery,
  SpentFiatPaymentIntentsQueryVariables,
} from './__generated__/index.graphql';

const messages = defineMessages({
  empty: {
    id: 'FiatTransactionsHistory.empty',
    defaultMessage: 'No credit card transactions',
  },
});

const SPENT_FIAT_PAYMENT_INTENTS_QUERY = gql`
  query SpentFiatPaymentIntentsQuery($cursor: String, $first: Int) {
    currentUser {
      slug
      authorizations(after: $cursor, first: $first) {
        nodes {
          id
          ...Offer_authorizationRequest
          ...Bid_authorizationRequest
          ...Auction_authorizationRequest
          ...PrimaryOffer_authorizationRequest
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Bid.fragments.payment}
  ${Auction.fragments.payment}
  ${Offer.fragments.payment}
  ${PrimaryOffer.fragments.payment}
` as TypedDocumentNode<
  SpentFiatPaymentIntentsQuery,
  SpentFiatPaymentIntentsQueryVariables
>;

type Props = {
  short?: boolean;
};

export const SpentFiatPaymentIntents = ({ short }: Props) => {
  const { data, loading, loadMore } = usePaginatedQuery(
    SPENT_FIAT_PAYMENT_INTENTS_QUERY,
    {
      variables: short ? { first: 5 } : undefined,
      connection: 'AuthorizationRequestConnection',
    }
  );

  const {
    items: payments = [],
    hasMore,
    cursor,
  } = extractConnectionData(data?.currentUser?.authorizations, src => src);

  if (!data && !loading) return <EmptyContent message={messages.empty} />;

  return (
    <div>
      {!data && loading ? (
        <LoadingIndicator />
      ) : (
        payments.map(payment => (
          <Fragment key={payment.id}>
            <Bid payment={payment} />
            <Auction payment={payment} />
            <Offer payment={payment} />
            <PrimaryOffer payment={payment} />
          </Fragment>
        ))
      )}
      {!short && (
        <LoadMoreButton
          hasMore={hasMore}
          loadMore={() => {
            loadMore(false, {
              cursor,
            });
          }}
          loading={loading}
        />
      )}
    </div>
  );
};
